<template>
  <div class="d-flex flex-column manage-filters w-100">
    <div class="d-flex flex-row align-items-start" v-for="(filter, i) in filters" :key="filter.id">
      <div class="where-col-1 text-right pt-1">
        <p v-if="i === 0" class="m-0 pt-2">Where</p>
        <b-form-select v-else-if="i === 1" :options="operatorOptions" v-model="operator"
          @change="value => updateOperator(value)"/>
      </div>
      <manage-filters v-if="typeof filter.operator === 'string'" :options="options"
        :value="filter" @input="value => updateFilter(i, value)"/>
      <filter-row v-else-if="typeof filter.hasOwnProperty('key')" :options="options"
        :value="filter" @input="value => updateFilter(i, value)"/>
      <b-btn-close class="text-muted mt-2 pt-1" @click.stop="() => removeFilter(i)"/>
    </div>
    <div class="d-flex flex-row align-items-center">
      <div class="where-col-1"/>
      <b-btn :class="{ 'my-2': filters.length === 0 }" @click.stop="() => addFilter()"
        class="ml-2" variant="link">+ Add a filter</b-btn>
    </div>
  </div>
</template>

<script>
import _debounce from 'lodash/debounce';
import _isPlainObject from 'lodash/isPlainObject';
import assert from 'http-assert-plus';
import ms from 'ms';
import { KeyValueHash, KeyValuePair, WhereManyInput } from '@src/models/where';

const createID = () => Math.ceil(Math.random() * 10000);
const defaultValues = {
  [Array]: [],
};

export default {
  name: 'ManageFilters',
  props: {
    options: {
      type: Object,
      required: true,
    },
    value: {
      type: Object,
      default: () => null,
    },
  },
  components: {
    FilterRow: {
      props: {
        options: {
          type: Object,
          required: true,
        },
        value: {
          type: Object,
          required: true,
        },
      },
      functional: true,
      render(r, { props: { options, value: filter }, listeners: { input: updateFilter } }) {
        const { value } = filter;
        const { [filter.key]: option = {} } = options;

        const debouncedUpdateFilter = _debounce(updateFilter, ms('500ms'));
        const divProps = {
          class: 'd-flex flex-row flex-fill',
        };

        const keyComponent = r('b-form-select', {
          class: 'where-key m-1',
          props: {
            options: [
              { text: '-- Choose --', value: null },
              ...Object.keys(options).reduce((list, key) => {
                if (options[key] && options[key].label) {
                  const { [key]: { label } } = options;
                  list.push({ text: label, value: key });
                }
                return list;
              }, []),
            ],
            value: filter.key || null,
          },
          on: {
            input(newKey) {
              const { [newKey]: newOption = {} } = options;
              const defaultValue = newOption.hasOwnProperty('default')
                ? (typeof newOption.default === 'function' ? newOption.default() : newOption.default)
                : (defaultValues.hasOwnProperty(newOption.type) ? defaultValues[newOption.type] : null);
              updateFilter({ key: newKey, value: defaultValue });
            },
          },
        });

        let valueComponent = null;
        if (option.type === String && Array.isArray(option.enum)) {
          valueComponent = r('b-form-select', {
            class: 'm-1',
            props: {
              options: [
                { text: '— Choose —', value: null },
                ...option.enum,
              ],
              value,
            },
            on: {
              input: newValue => updateFilter({ key: filter.key, value: newValue }),
            },
          });
        } else if (option.type === Array && option.item === String) {
          valueComponent = r('b-form-tags', {
            class: 'm-1 py-0',
            props: {
              'add-button-variant': 'link',
              'no-tag-remove': true,
              placeholder: option.placeholder || '—',
              'remove-on-delete': true,
              'tag-pills': true,
              'tag-variant': 'light',
              value: Array.isArray(value) ? value : [],
            },
            on: {
              input: newValue => updateFilter({ key: filter.key, value: newValue }),
            },
          });
        } else if ((option.type === Array && option.item === KeyValuePair) || option.type === KeyValueHash) {
          divProps.class = 'd-flex flex-column flex-fill';
          valueComponent = r('b-card', {
            class: 'm-1 pr-1',
            props: { 'body-class': 'p-1' },
          }, [
            r(KeyValueFilter, {
              class: 'm-1',
              props: {
                isKeyValueHash: option.type === KeyValueHash,
                value,
              },
              on: {
                input: newValue => debouncedUpdateFilter({ key: filter.key, value: newValue }),
              },
            }),
          ]);
        } else if (option.type === KeyValuePair) {
          const { key: pairKey, value: pairValue } = value || {};
          valueComponent = r('div', { class: 'd-flex flex-row align-items-center flex-fill' }, [
            r('b-form-input', {
              class: 'm-1',
              props: { placeholder: 'Key', value: pairKey || null },
              on: { input: newKey => debouncedUpdateFilter({ key: filter.key, value: { key: newKey, value: pairValue } }) },
            }),
            ':',
            r('b-form-input', {
              class: 'm-1',
              props: { placeholder: 'Value', value: pairValue || null },
              on: { input: newValue => debouncedUpdateFilter({ key: filter.key, value: { key: pairKey, value: newValue } }) },
            }),
          ]);
        } else if (option.type === WhereManyInput) {
          divProps.class = 'd-flex flex-column flex-fill';
          valueComponent = r('b-card', {
            class: 'm-1 pr-1',
            props: { 'body-class': 'p-1' },
          }, [
            r('manage-filters', {
              class: 'm-1',
              props: {
                options: option.options,
                value,
              },
              on: {
                input: newValue => debouncedUpdateFilter({ key: filter.key, value: newValue }),
              },
            }),
          ]);
        } else if (option.type === Boolean) {
          valueComponent = r('b-form-checkbox', {
            class: 'my-2 mx-1 py-2',
            props: {
              checked: value,
            },
            on: {
              input: newValue => debouncedUpdateFilter({ key: filter.key, value: newValue }),
            },
          }, option.description || '');
        } else if (option.type === String || option.type === Number) {
          valueComponent = r('b-form-input', {
            class: 'm-1',
            props: {
              placeholder: '—',
              value,
            },
            on: {
              input: newValue => debouncedUpdateFilter({ key: filter.key, value: newValue }),
            },
          });
        } else {
          valueComponent = r('div', { class: 'd-flex flex-column flex-fill' });
        }

        return r('div', divProps, [ keyComponent, valueComponent ]);
      },
    },
  },
  data: () => ({
    operator: 'AND',
    filters: [],
  }),
  created() {
    if (this.value && this.isValueTransformed) {
      const { operator, filters } = this.value;
      this.operator = operator;
      this.filters = filters;
    } else if (this.value) {
      const { operator, filters } = fromValue(this.options, this.value);
      this.operator = operator;
      this.filters = filters;
    }
  },
  computed: {
    isValueTransformed() {
      return this.value && typeof this.value.operator === 'string' && Array.isArray(this.value.filters);
    },
    operatorOptions: () => ([
      { text: 'And', value: 'AND' },
      { text: 'Or', value: 'OR' },
      { text: 'Not', value: 'NOT' },
    ]),
    fieldOptions() {
      return [
        { text: '-- Choose --', value: null },
        ...Object.keys(this.options).reduce((list, key) => {
          if (this.options[key] && this.options[key].label) {
            const { [key]: { label } } = this.options;
            list.push({ text: label, value: key });
          }
          return list;
        }, []),
      ];
    }
  },
  methods: {
    updateOperator(operator) {
      this.operator = operator;
      this._emitNewValue();
    },
    addFilter() {
      this.filters.push({ id: createID(), key: null });
      this._emitNewValue();
    },
    updateFilter(i, update) {
      this.filters[i] = { ...this.filters[i], ...update };
      this.$forceUpdate();
      this._emitNewValue();
    },
    removeFilter(i) {
      this.filters.splice(i, 1);
      if (this.filters.length === 1) {
        this.operator = 'AND';
      }
      this._emitNewValue();
    },
    _emitNewValue() {
      const { operator, filters } = this;
      if (this.isValueTransformed) {
        this.$emit('input', { operator, filters });
      } else {
        const value = toValue(this.options, { operator, filters });
        this.$log.debug(JSON.stringify(value));
        this.$emit('input', value);
      }
    },
  },
};

function fromValue(options, { AND, OR, NOT, ...where }) {
  assert(!AND || Array.isArray(AND) || _isPlainObject(AND), 'Expected AND to be an array or object');
  assert(!OR || Array.isArray(OR) || _isPlainObject(OR), 'Expected OR to be an array or object');
  assert(!NOT || Array.isArray(NOT) || _isPlainObject(NOT), 'Expected NOT to be an array or object');
  assert(!where || _isPlainObject(where), 'Expected { where } to be an object');

  const statement = {
    operator: 'AND',
    filters: [],
  };

  const eachWhereInput = where => Object.keys(where).reduce((filters, key) => {
    if (options[key] && options[key].type) {
      filters.push({ id: createID(), key, value: where[key] });
    }
    return filters;
  }, []);

  if (options.AND && AND) {
    statement.filters = statement.filters.concat((Array.isArray(AND) ? AND : [ AND ])
      .reduce((fs, f) => fs.concat(eachWhereInput(f)), []));
  }
  if (options.OR && OR) {
    statement.filters.push({
      operator: 'OR',
      filters: (Array.isArray(OR) ? OR : [ OR ]).reduce((fs, f) => fs.concat(eachWhereInput(f)), []),
    });
  }
  if (options.NOT && NOT) {
    statement.filters.push({
      operator: 'NOT',
      filters: (Array.isArray(OR) ? OR : [ OR ]).reduce((fs, f) => fs.concat(eachWhereInput(f)), []),
    });
  }
  if (where) {
    statement.filters = statement.filters.concat(eachWhereInput(where));
  }

  if (statement.filters.length === 1 && statement.filters[0] && statement.filters[0].operator) {
    return statement.filters.shift();
  } else {
    return statement;
  }
}

function toValue(options, statement) {
  assert(_isPlainObject(statement), new TypeError('Expected statement to be an object'));
  assert(typeof statement.operator === 'string', new TypeError('Expected statement to have an operator'));
  assert(Array.isArray(statement.filters), new TypeError('Expected statement to have filters'));

  const buildWhere = (where, block) => {
    if (block && typeof block.operator === 'string' && Array.isArray(block.filters)) {
      const { operator, filters } = block;
      assert([ 'AND', 'OR', 'NOT' ].includes(operator), new TypeError(`Invalid operator: ${operator}`));
      where[operator] = filters.reduce(buildWhere, {});
    } else if (block && typeof block.key === 'string') {
      const { key, value } = block;
      assert(typeof key === 'string', new TypeError('Expected each block key to be a string'));
      assert(options[key], new TypeError(`Unknown key: ${key}`));

      if (typeof value === 'object' && Object.keys(value).length === 1) {
        const [ valueKey ] = Object.keys(value);
        where[key] = { key: valueKey, value: value[valueKey] };
      } else {
        where[key] = value;
      }
    }

    return where;
  };

  const collapseStatements = (collapsed, value) => {
    try {
      assert(collapsed, 'Expected collapsed to be an object');

      const keys = Object.keys(value);
      // If there isn't a single key that isn't AND, OR, NOT then fail
      assert(keys.length === 1 && !keys.includes('AND') && !keys.includes('OR') && !keys.includes('NOT'),
        'Expected a single key');

      const [ key ] = keys;
      assert(!collapsed.hasOwnProperty(key), 'Collapsed cannot support duplicate keys');

      return { ...collapsed, ...value };
    } catch (err) {
      // this.$log.error(err);
      return null;
    }
  };

  statement.filters = statement.filters.filter(filter => filter && filter.key);

  if (statement.operator === 'AND' && statement.filters.length) {
    const AND = statement.filters.map(f => buildWhere({}, f)).filter(v => v && Object.keys(v).length);
    return AND.reduce(collapseStatements, {}) || { AND };
  } else if (statement.operator === 'OR' && statement.filters.length) {
    const OR = statement.filters.map(f => buildWhere({}, f)).filter(v => v && Object.keys(v).length);
    return OR.length === 1 ? OR.shift() : { OR };
  } else if (statement.operator === 'NOT' && statement.filters.length) {
    const NOT = statement.filters.map(f => buildWhere({}, f)).filter(v => v && Object.keys(v).length);
    return { NOT };
  } else {
    return null;
  }
}

const KeyValueFilter = {
  props: {
    isKeyValueHash: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [ Array, Object ],
      default: () => null,
    },
  },
  data: () => ({
    keyValueArray: [],
  }),
  created() {
    this.keyValueArray = this.isKeyValueHash
      ? Object.keys(this.value || {}).map(key => ({ key, value: this.value[key] }))
      : (Array.isArray(this.value) ? this.value : []).map(({ key, value }) => ({ key, value }));
  },
  methods: {
    addKeyValueRow() {
      this.keyValueArray.push({});
      this.updateInput();
    },
    updateKeyValueRow(i, update) {
      this.keyValueArray[i] = { ...this.keyValueArray[i], ...update };
      this.updateInput();
    },
    removeKeyValueRow(i) {
      this.keyValueArray.splice(i, 1);
      this.updateInput();
    },
    updateInput() {
      const keyValueArray = this.keyValueArray.filter(r => r && r['key'] && typeof r['key'] === 'string');
      if (keyValueArray.length) {
        this.$emit('input', this.isKeyValueHash
          ? keyValueArray.reduce((o, { key, value }) => ({ ...o, [key]: value }), {})
          : keyValueArray.map(({ key, value }) => ({ key, value })));
      } else {
        this.$emit('input', null);
      }
    },
  },
  render(el) {
    return el('div', { class: 'd-flex flex-column' }, [
      ...this.keyValueArray.map(({ key, value }, i) => el('div', { class: 'd-flex flex-row align-items-center' }, [
        el('b-form-input', {
          class: 'm-1',
          props: { placeholder: 'Key', value: key || null },
          on: { input: newKey => this.updateKeyValueRow(i, { key: newKey }) },
        }),
        ':',
        el('b-form-input', {
          class: 'm-1',
          props: { placeholder: 'Value', value: value || null },
          on: { input: newValue => this.updateKeyValueRow(i, { value: newValue }) },
        }),
        el('b-btn-close', {
          class: 'text-muted',
          props: { size: 'sm' },
          on: { click: () => this.removeKeyValueRow(i) },
        }),
      ])),
      el('div', { class: 'd-flex flex-row align-items-center' }, [
        el('b-btn', {
          class: 'text-left text-primary',
          props: { variant: 'link' },
          on: { click: () => this.addKeyValueRow() },
        }, '+ Add row'),
      ]),
    ]);
  },
};
</script>

<style lang="scss">
.manage-filters {
  .where-col-1 {
    width: 70px;
    min-width: 70px;
    max-width: 70px;
  }

  .where-key {
    width: 120px;

    @media (min-width: 576px) {
      width: 250px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/manage/Filters.vue